import { useMemo } from 'react';
import { t } from '@lingui/macro';
import clsx from 'clsx';
import { makeStyles, Typography } from '@material-ui/core';

import PerformanceChart from '../../charts/performance-chart/PerformanceChart';

const EXAMPLE_METRIC_DATA = [15, 14, 6, 25, 17, 13, 15, 19, 11, 15];
const CHART_GRANULARITY = 60_000;
const CHART_START_TIME = Date.UTC(2020, 0, 1);

const useStyles = makeStyles((theme) => ({
    chartContainer: {
        height: 200,
        width: 400,
        maxWidth: 400,
        margin: '0 auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1),
        boxSizing: 'border-box',
    },
    chartTitle: {
        textAlign: 'center',
        marginBottom: theme.spacing(0.5),
    },
}));

function asNumber(value) {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function getSensitivityMultipliers(sensitivity) {
    switch (asNumber(sensitivity)) {
        case 1:
            return { degraded: 0.1, excessive: 0.2 };
        case 2:
            return { degraded: 0.2, excessive: 0.4 };
        case 3:
            return { degraded: 0.4, excessive: 0.8 };
        case 4:
            return { degraded: 0.8, excessive: 1.6 };
        case 5:
            return { degraded: 1.6, excessive: 3.2 };
        default:
            return { degraded: 0.8, excessive: 1.6 };
    }
}

function buildChartData(sensitivity) {
    const average = EXAMPLE_METRIC_DATA.reduce((sum, value) => sum + value, 0) / EXAMPLE_METRIC_DATA.length;
    const multipliers = getSensitivityMultipliers(sensitivity);
    const degradedThreshold = average + average * multipliers.degraded;
    const excessiveThreshold = average + average * multipliers.excessive;

    const buildThresholdData = (thresholdValue) =>
        EXAMPLE_METRIC_DATA.map((_, index) => ({
            date: CHART_START_TIME + index * CHART_GRANULARITY,
            value: thresholdValue,
        }));

    const metricData = EXAMPLE_METRIC_DATA.map((value, index) => ({
        date: CHART_START_TIME + index * CHART_GRANULARITY,
        value,
    }));

    const statusData = EXAMPLE_METRIC_DATA.map((value, index) => {
        let worst = 'green';
        if (value >= excessiveThreshold) {
            worst = 'orange';
        } else if (value >= degradedThreshold) {
            worst = 'yellow';
        }

        return {
            date: CHART_START_TIME + index * CHART_GRANULARITY,
            value: {
                count: 1,
                gray: 0,
                green: worst === 'green' ? 100 : 0,
                yellow: worst === 'yellow' ? 100 : 0,
                orange: worst === 'orange' ? 100 : 0,
                red: 0,
                worst,
            },
        };
    });

    return {
        trends: {
            metric: {
                granularity: CHART_GRANULARITY,
                data: metricData,
            },
            degraded: {
                granularity: CHART_GRANULARITY,
                data: buildThresholdData(degradedThreshold),
            },
            excessive: {
                granularity: CHART_GRANULARITY,
                data: buildThresholdData(excessiveThreshold),
            },
            status: {
                granularity: CHART_GRANULARITY,
                data: statusData,
            },
        },
    };
}

export default function ThresholdSensitivityExampleChart({ sensitivity }) {
    const classes = useStyles();

    const chartDef = useMemo(
        () => ({
            chartType: 'line',
            legendCursor: false,
            statusMetric: 'status',
            yAxis: [
                {
                    max: 70,
                    title: {
                        text: '',
                    },
                    labels: {
                        enabled: false,
                    },
                },
            ],
            chartTrends: [
                {
                    key: 'metric',
                    chartType: 'line',
                    label: t`Metric`,
                    color: '#7cb5ec',
                },
                {
                    key: 'degraded',
                    chartType: 'line',
                    label: t`Degraded`,
                    color: '#fbc02d',
                },
                {
                    key: 'excessive',
                    chartType: 'line',
                    label: t`Excessive`,
                    color: '#f57c00',
                },
            ],
        }),
        []
    );

    const chartData = useMemo(() => buildChartData(sensitivity), [sensitivity]);

    return (
        <div className={clsx('flex-container', classes.chartContainer)}>
            <Typography variant="subtitle2" className={classes.chartTitle}>
                {t`Sensitivity Example`}
            </Typography>
            <PerformanceChart
                chartDef={chartDef}
                data={chartData}
                initialHeight={140}
                disableYAxis
                xAxis={{
                    labels: { enabled: false },
                    lineWidth: 0,
                    tickLength: 0,
                    gridLineWidth: 0,
                }}
            />
        </div>
    );
}
